/**
 * The v1/v2 ownership map and the path arithmetic both gates do with it.
 *
 * Pure and dependency-free on purpose: the same functions exist in the new
 * app (marta-platform/apps/family-app/src/features/cutover/lib/route-map.ts)
 * and the validation half lives in appconfig-service. The copies are not
 * shared code — the repos do not depend on each other — so the behaviour is
 * pinned by identical tests rather than by a package. Keep this file and its
 * test in step with the new app's whenever either moves.
 *
 * `buildV1Url` is the one function this copy leaves out: only the new app
 * ever forwards in that direction.
 */

/**
 * `exact` narrows a rule to the path it names. Without it a rule owns its
 * whole subtree, which is right for a section and wrong for a screen that has
 * one: Documents, Invoices and Settings all live under the profile stack
 * here, and a plain `profile` rule would bounce every one of them to the new
 * app's Profile.
 *
 * A rule without `exact` also collapses the subtree it owns onto its target:
 * whatever follows the matched path is dropped, not carried across. Every URL
 * under `home` lands on the one `target` the rule names, which is what the
 * live map relies on to send the old Home stack to the new app's onboarding.
 */
export type Rule = { match: string; target: string; exact?: boolean };
export type RouteMap = { v1ToV2: Rule[]; v2ToV1: Rule[] };

/** Slashes are meaningless at either end of a path we compare by segment, and
 *  the map is hand-edited JSON in a database row — "/menu/documents/" and
 *  "menu/documents" have to mean the same rule. */
const trimSlashes = (value: string): string =>
  value.replace(/^\/+/, "").replace(/\/+$/, "");

/**
 * Splits a browser path into its locale segment and the rest, which is what
 * the map is keyed on.
 *
 * The base path goes first and only when it actually prefixes a whole
 * segment: a substring check would eat the first characters of an unrelated
 * path. This app is served at the root, so its callers pass no base path.
 */
export function stripLocale(
  path: string,
  basePath = "",
): { locale: string | null; rest: string } {
  let remainder = path;
  if (
    basePath &&
    (remainder === basePath || remainder.startsWith(`${basePath}/`))
  ) {
    remainder = remainder.slice(basePath.length);
  }

  const segments = trimSlashes(remainder).split("/").filter(Boolean);
  // Two lowercase letters is the whole locale vocabulary both apps generate
  // ("de", "en"); anything else is a screen whose name happens to be short.
  if (segments.length > 0 && /^[a-z]{2}$/.test(segments[0])) {
    return { locale: segments[0], rest: segments.slice(1).join("/") };
  }
  return { locale: null, rest: segments.join("/") };
}

/**
 * The rule that owns `rest`, or null.
 *
 * Prefix match on segment boundaries — "menu" owns "menu" and "menu/x" but
 * not "menu-x" — unless the rule is `exact`, which owns only the path it
 * names. The longest match wins among whichever rules matched, so a rule for
 * a single screen can sit under a rule for its whole section regardless of
 * list order; `exact` narrows what matches, never what wins.
 */
export function resolve(rules: Rule[], rest: string): Rule | null {
  const path = trimSlashes(rest);
  let best: Rule | null = null;
  let bestLength = -1;

  for (const rule of rules) {
    const match = trimSlashes(rule.match);
    const owns = rule.exact
      ? path === match
      : path === match || path.startsWith(`${match}/`);

    if (!owns) continue;
    if (match.length > bestLength) {
      best = rule;
      bestLength = match.length;
    }
  }

  return best;
}

const withLocale = (locale: string | null, target: string): string =>
  locale ? `/${locale}/${trimSlashes(target)}` : `/${trimSlashes(target)}`;

/** The new app's URL for a rule. The base path is a parameter rather than an
 *  env read so the function stays pure and both apps can use the same code.
 *  Query and hash travel unchanged — they carry reset hashes and lead tokens
 *  the destination screen needs. */
export function buildV2Url(
  basePath: string,
  rule: Rule,
  locale: string | null,
  search: string,
  hash: string,
): string {
  return `${basePath}${withLocale(locale, rule.target)}${search}${hash}`;
}

/**
 * A backslash, or a leading "//", turns a path into an authority: WHATWG URL
 * parsing on an https base reads a backslash after the leading slash as a
 * second slash and resolves the result to https://evil.com/.
 * A target is concatenated into the URL the gate hands
 * to location.assign, and the map is a hand-edited database row, so a rule
 * that can escape the origin is an open redirect. ":" would let a target
 * carry its own scheme, and "?", "#" and whitespace would let it rewrite the
 * query, the fragment, or the parse itself — none of them belong in a path
 * the map names. Nor does "%": see hasUnsafeSegment.
 */
const UNSAFE_IN_PATH = /[\\:?#\s%]/;

/**
 * A segment the browser resolves rather than keeps. `..` and `.` let a target
 * climb out of the base path — `/v2/de/../../home` is `/home`, a route of this
 * app, which forwards again: an infinite reload for everyone. An empty
 * segment inside the path is no route either. `%` is refused outright above,
 * because `%2e%2e` is `..` by another spelling and nothing the map names
 * needs an escape.
 */
const hasUnsafeSegment = (value: string) =>
  trimSlashes(value)
    .split("/")
    .some((segment) => segment === "" || segment === "." || segment === "..");

const isSafePath = (value: string) =>
  // An empty match would own every route, including the ones this app still
  // serves, and there is no way back once the browser has left.
  trimSlashes(value) !== "" &&
  !UNSAFE_IN_PATH.test(value) &&
  !value.startsWith("//") &&
  !hasUnsafeSegment(value);

const isRule = (value: unknown): value is Rule => {
  if (typeof value !== "object" || value === null) return false;
  const { match, target, exact } = value as Record<string, unknown>;
  return (
    typeof match === "string" &&
    typeof target === "string" &&
    isSafePath(match) &&
    isSafePath(target) &&
    // Absent is the prefix default. Anything that is neither absent nor a
    // boolean is a flag this app cannot read, and reading it wrong is the
    // difference between owning a screen and owning everything under it.
    (exact === undefined || typeof exact === "boolean")
  );
};

const isRuleList = (value: unknown): value is Rule[] =>
  Array.isArray(value) && value.every(isRule);

/**
 * Whether a built URL still points at this origin.
 *
 * The last check before the browser leaves, and deliberately not a repeat of
 * the parser's: the parser judges the rule, this judges the string that was
 * actually assembled from it. Anything the URL parser reads as another origin
 * — or cannot read at all — is not somewhere this app forwards to.
 */
export function isSameOriginPath(url: string, origin: string): boolean {
  try {
    return new URL(url, origin).origin === origin;
  } catch {
    return false;
  }
}

/**
 * The toggle payload as a map, or null when it is anything else.
 *
 * All or nothing: appconfig validates the map before serving it, so a
 * malformed payload here means the two are out of step — which is exactly
 * when forwarding must stop rather than guess which half is still good. Null
 * is the same answer the callers give a disabled toggle: no forwarding.
 */
export function parseRouteMap(payload: unknown): RouteMap | null {
  if (typeof payload !== "object" || payload === null) return null;
  const { v1ToV2, v2ToV1 } = payload as Record<string, unknown>;
  if (!isRuleList(v1ToV2) || !isRuleList(v2ToV1)) return null;
  return { v1ToV2, v2ToV1 };
}
